/**
 * Internal utilities for monetary value serialization.
 *
 * These functions are shared between MonetarySchemas (validation) and
 * MonetaryInjections (formatting). They are NOT intended for general use.
 *
 * Input preconditions: All string parameters must be valid decimal strings:
 * - Optional leading minus sign
 * - One or more digits
 * - At most one decimal point
 * - If decimal point present, followed by one or more digits
 * - Examples: "123", "-45.67", "0", "0.001", "-0"
 */
/**
 * Counts the number of digits after the decimal point.
 * Returns 0 if there is no decimal point.
 *
 * @param value - A valid decimal string (see module documentation)
 * @example countDecimalPlaces("123") === 0
 * @example countDecimalPlaces("123.45") === 2
 * @example countDecimalPlaces("123.0") === 1
 */
export function countDecimalPlaces(value) {
    const decimalIndex = value.indexOf('.');
    return decimalIndex === -1 ? 0 : value.length - decimalIndex - 1;
}
/**
 * Validates a decimal value string against the currency-aware predicate.
 *
 * Predicate: valid(v, m) := n(v) = 0 OR n(v) = m OR (n(v) > m AND lastDigit(v) != '0')
 *
 * Where:
 *   - n(v) = number of decimal places
 *   - m = minorUnits for the currency
 *   - lastDigit(v) = the final character after the decimal point
 *
 * Valid cases:
 *   - No decimal places (e.g., "100" for any currency)
 *   - Exactly minorUnits decimal places (e.g., "1.00" for USD)
 *   - More than minorUnits with non-zero last digit (e.g., "1.234" for USD)
 *
 * Invalid cases:
 *   - Fewer than minorUnits decimal places (except 0), e.g., "1.0" for USD
 *   - Trailing zeros beyond minorUnits, e.g., "1.230" for USD
 *
 * @param value - A valid decimal string (see module documentation)
 * @param minorUnits - The currency's minor units (decimal places)
 */
export function isValidDecimalForMinorUnits(value, minorUnits) {
    const decimalPlaces = countDecimalPlaces(value);
    return decimalPlaces === 0 || decimalPlaces === minorUnits || (decimalPlaces > minorUnits && value.at(-1) !== '0');
}
/**
 * Formats a Decimal value for serialization according to currency-aware rules.
 *
 * Serialization rules:
 * - At least minorUnits decimal places (pad with zeros if needed)
 * - No trailing zeros beyond minorUnits (strip them)
 *
 * Output is guaranteed to pass isValidDecimalForMinorUnits validation.
 *
 * @param value - A Decimal.js value
 * @param minorUnits - The currency's minor units (decimal places)
 *
 * @example formatDecimalForCurrency(Decimal(1), 2) === "1.00"
 * @example formatDecimalForCurrency(Decimal(1.5), 2) === "1.50"
 * @example formatDecimalForCurrency(Decimal(1.234), 2) === "1.234"
 * @example formatDecimalForCurrency(Decimal(1.230), 2) === "1.23"
 * @example formatDecimalForCurrency(Decimal(100), 0) === "100"
 * @example formatDecimalForCurrency(Decimal(100.5), 0) === "100.5"
 */
export function formatDecimalForCurrency(value, minorUnits) {
    const str = value.toString();
    const decimalIndex = str.indexOf('.');
    // No decimal point - add decimal places if needed
    if (decimalIndex === -1) {
        return minorUnits === 0 ? str : `${str}.${'0'.repeat(minorUnits)}`;
    }
    // Split into integer and fractional parts
    const intPart = str.slice(0, decimalIndex);
    let fracPart = str.slice(decimalIndex + 1);
    if (fracPart.length < minorUnits) {
        // Pad with zeros to reach minorUnits
        fracPart = fracPart.padEnd(minorUnits, '0');
    }
    else if (fracPart.length > minorUnits) {
        // Strip trailing zeros, keeping at least minorUnits characters
        while (fracPart.length > minorUnits && fracPart.endsWith('0')) {
            fracPart = fracPart.slice(0, -1);
        }
    }
    // Return integer only if no fractional part remains
    return fracPart.length === 0 ? intPart : `${intPart}.${fracPart}`;
}
//# sourceMappingURL=MonetaryUtils.js.map